import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import axios from "axios";
import { useTranslation } from "react-i18next";

const API = process.env.REACT_APP_BACKEND_URL;

// Public page — reached from Stripe Checkout redirect (web OR mobile browser).
export default function PaymentSuccessPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling"); // polling | paid | failed | timeout
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    let stop = false;
    const poll = async () => {
      if (stop) return;
      attempts.current += 1;
      try {
        const { data } = await axios.get(`${API}/api/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setState("failed"); return; }
      } catch { /* keep polling */ }
      if (attempts.current >= 10) { setState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, [sessionId]);

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#0d1b2a", padding: "1rem" }} data-testid="payment-success-page">
      <div className="gold-card" style={{ maxWidth: 420, width: "100%", padding: "2rem", textAlign: "center", background: "#fdfaf3" }}>
        {state === "polling" && (
          <>
            <Loader2 size={44} className="animate-spin" style={{ margin: "0 auto 1rem", color: "#9a7530" }} />
            <h1 style={{ fontFamily: "Cinzel, serif", fontSize: "1.15rem", color: "#0d1b2a" }}>
              {t("payments.verifying", "Verificăm plata...")}
            </h1>
            <p className="kv-label" style={{ marginTop: 8 }}>{t("payments.verifyingHint", "Nu închide această pagină.")}</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 size={48} style={{ margin: "0 auto 1rem", color: "#3e6a3c" }} />
            <h1 style={{ fontFamily: "Cinzel, serif", fontSize: "1.2rem", color: "#0d1b2a" }} data-testid="payment-paid-title">
              {t("payments.successTitle", "Plată confirmată")}
            </h1>
            <p className="kv-label" style={{ marginTop: 8 }}>
              {t("payments.successBody", "Mulțumim! Plata a fost înregistrată. Poți închide această pagină și reveni în aplicație.")}
            </p>
          </>
        )}
        {(state === "failed" || state === "timeout") && (
          <>
            <XCircle size={48} style={{ margin: "0 auto 1rem", color: "#7c2a2a" }} />
            <h1 style={{ fontFamily: "Cinzel, serif", fontSize: "1.2rem", color: "#0d1b2a" }}>
              {state === "timeout"
                ? t("payments.timeoutTitle", "Verificarea durează mai mult")
                : t("payments.failedTitle", "Plata nu a fost confirmată")}
            </h1>
            <p className="kv-label" style={{ marginTop: 8 }}>
              {state === "timeout"
                ? t("payments.timeoutBody", "Plata poate fi încă în procesare — statusul se actualizează automat în aplicație.")
                : t("payments.failedBody", "Dacă ai fost debitat, contactează secretarul lojei.")}
            </p>
          </>
        )}
        <Link to="/" className="btn-outline-gold" style={{ display: "inline-block", marginTop: "1.25rem" }}>
          {t("payments.backHome", "Înapoi la aplicație")}
        </Link>
      </div>
    </div>
  );
}
